/**
 * Engagement Analytics Section
 * Shows analytics metrics for a single engagement
 */

import { useDashboardData } from '@/hooks/use-dashboard-data'
import { useBranches } from '@/hooks/use-branches'
import { ClientInfoBar } from '@/components/dashboard/ClientInfoBar'
import { DashboardKPICards } from '@/components/dashboard/DashboardKPICards'
import { BurndownChart } from '@/components/dashboard/BurndownChart'
import { ProfitabilityChart } from '@/components/dashboard/ProfitabilityChart'
import { ContractorTypeChart } from '@/components/dashboard/ContractorTypeChart'
import { RemainingContractWidget } from '@/components/dashboard/RemainingContractWidget'
import { RevenueRecognizedChart } from '@/components/dashboard/RevenueRecognizedChart'
import { GrossProfitChart } from '@/components/dashboard/GrossProfitChart'
import { TeamHoursTable } from '@/components/dashboard/TeamHoursTable'

interface EngagementAnalyticsSectionProps {
  engagementId?: number
}

export function EngagementAnalyticsSection({ engagementId }: EngagementAnalyticsSectionProps) {
  // Fetch branches for name lookup
  const { branches } = useBranches()

  const {
    metrics,
    monthlyData,
    engagementInfo: baseEngagementInfo,
    branchCode,
    isLoading,
    error,
    workerTypeData,
    teamHoursData,
    teamHoursLoading,
    teamHoursError,
  } = useDashboardData({
    selectedEngagementId: engagementId,
  })

  // Look up branch name from code
  const engagementInfo = baseEngagementInfo ? {
    ...baseEngagementInfo,
    branch: branchCode 
      ? branches.find(b => b.Code === branchCode)?.Name || branchCode
      : '—'
  } : null

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">
          Error loading analytics data: {typeof error === 'string' ? error : 'An unexpected error occurred'}
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading analytics data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6" id="analytics-dashboard">
      {/* Client Info Bar */}
      <ClientInfoBar engagementInfo={engagementInfo} engagementId={engagementId || 0} />

      {/* KPI Cards */}
      <DashboardKPICards metrics={metrics} teamHoursData={teamHoursData} />

      {/* Charts - 3 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <BurndownChart 
          data={monthlyData} 
          contractAmount={metrics.contractAmount}
          projectedCost={metrics.projectedCost}
          engagementId={engagementId}
        />
        <ProfitabilityChart 
          data={monthlyData} 
          contractAmount={metrics.contractAmount}
          projectedCost={metrics.projectedCost}
          engagementId={engagementId}
        />
        <ContractorTypeChart 
          data={workerTypeData} 
          engagementId={engagementId}
        />
      </div>

      {/* Remaining Contract + Revenue Recognized + Gross Profit - 3 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RemainingContractWidget metrics={metrics} monthlyData={monthlyData} />
        <GrossProfitChart 
          data={monthlyData}
          contractAmount={metrics.contractAmount}
          projectedCost={metrics.projectedCost}
          engagementId={engagementId}
        />
        <RevenueRecognizedChart 
          data={monthlyData} 
          billedToDate={metrics.billedToDate}
          revenueToDate={metrics.revenueToDate}
          engagementId={engagementId}
        />
      </div>
      {/* Team Hours Table - Full width */}
      <TeamHoursTable 
        data={teamHoursData} 
        isLoading={teamHoursLoading}
        engagementId={engagementId}
      />
      {teamHoursError && (
        <p className="text-sm text-red-600 mt-2">
          Error loading team hours: {teamHoursError}
        </p>
      )}
    </div>
  )
}

